import { Environment } from '../../../environment';
import { v4 as uuidv4 } from 'uuid';
import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';
import { IOrdemServicoItem } from './OrdemServicoProdutosService';
import { IOrdemServicoMaoDeObra } from './OrdemServicoMaoDeObraService';
import { IOrdemServicoFoto } from './OrdemServicoFotosService';

export type TOSStatus =
  | 'ABERTA'
  | 'EM_DIAGNOSTICO'
  | 'AGUARDANDO_APROVACAO'
  | 'APROVADA'
  | 'EM_EXECUCAO'
  | 'AGUARDANDO_PECA'
  | 'CONCLUIDA'
  | 'ENTREGUE'
  | 'CANCELADA';

export interface IListagemOrdemServico {
  idos: string;
  numero: string;
  emissao: string;
  status: TOSStatus;
  total: number;
  previsaoentrega?: string;
  nomeparceiro: string;
  descricaobem: string;
  tipobem: string;
  placa?: string;
  patrimonio?: string;
}

export interface IDetalheOrdemServico {
  idos: string;
  numero: string;
  idparceiro: string;
  idbem: string;
  status: TOSStatus;
  emissao: string;
  relatocliente: string;
  defeitoconstatado?: string;
  kmentrada?: number;
  horimetroentrada?: number;
  nivelcombustivel?: string;
  condicaoentrada?: string;
  acessorios?: string;
  observacoesentrada?: string;
  previsaoentrega?: string;
  vservicos: number;
  vprodutos: number;
  desconto: number;
  acrescimo: number;
  total: number;
  nomeparceiro: string;
  cpfparceiro: string;
  descricaobem: string;
  tipobem: string;
  marca?: string;
  modelo?: string;
  placa?: string;
  patrimonio?: string;
  numeroserie?: string;
  itens: IOrdemServicoItem[];
  maodeobra: IOrdemServicoMaoDeObra[];
  fotos: IOrdemServicoFoto[];
}

// vservicos não entra aqui: virou a soma das linhas de maodeobra (ver
// OrdemServicoMaoDeObraService.ts), sempre recalculado pelo backend.
export interface IUpdateOrdemServico {
  relatocliente?: string;
  defeitoconstatado?: string;
  observacoesentrada?: string;
  desconto?: number;
  acrescimo?: number;
}

export interface ICreateOrdemServico {
  idparceiro: string;
  idbem: string;
  relatocliente: string;
  kmentrada?: number;
  horimetroentrada?: number;
  nivelcombustivel?: string;
  condicaoentrada?: string;
  acessorios?: string;
  observacoesentrada?: string;
  previsaoentrega?: string;
}

type TotalCount = {
  data: IListagemOrdemServico[];
  totalCount: number;
};

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const tratarErro = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response) {
      return new Error(axiosError.response.data.errors?.default || 'Erro desconhecido');
    } else if (axiosError.request) {
      return new Error('Nenhuma resposta do servidor');
    } else {
      return new Error(axiosError.message);
    }
  }
  return new Error('Erro inesperado');
};

const getAll = async (page = 1, filter = '', status = ''): Promise<TotalCount | Error> => {
  try {
    const urlRelativa = `/ordemservico?page=${page}&limit=${Environment.LIMITE_DE_LINHAS}&filter=${filter}&status=${status}`;
    const { data, headers } = await Api.get(urlRelativa);
    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || Environment.LIMITE_DE_LINHAS),
      };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    return tratarErro(error);
  }
};

const getById = async (id: string): Promise<IDetalheOrdemServico | Error> => {
  try {
    const { data } = await Api.get(`/ordemservico/${id}`);
    if (data) return data;
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    return tratarErro(error);
  }
};

const create = async (dados: ICreateOrdemServico): Promise<{ idos: string; numero: string } | Error> => {
  try {
    const guuid = uuidv4();
    const { data } = await Api.post('/ordemservico', { idos: guuid, ...dados });
    if (data?.idos) return data;
    return new Error('Erro ao criar o registro.');
  } catch (error) {
    return tratarErro(error);
  }
};

const updateById = async (id: string, dados: IUpdateOrdemServico): Promise<void | Error> => {
  try {
    await Api.put(`/ordemservico/${id}`, dados);
  } catch (error) {
    return tratarErro(error);
  }
};

// Transição validada no backend (ver UpdateStatus.ts) — não aceita
// qualquer status vindo do front, só o que a OS atual permite.
const updateStatus = async (id: string, status: TOSStatus, descricao?: string): Promise<void | Error> => {
  try {
    await Api.put(`/ordemservico/${id}/status`, { status, descricao });
  } catch (error) {
    return tratarErro(error);
  }
};

export const OrdemServicoService = {
  getAll,
  getById,
  create,
  updateById,
  updateStatus,
};
